import { useState, useEffect, useRef } from 'react';
import useElapsedTime from '../use-elapsed-time';
import { useWakeLock } from '../use-wake-lock';
import { getSystemAudioErrorMessage, hasSystemAudioTrack, missingSystemAudioTrackMessage, stopSharing } from './system-audio-error';

// Type declaration for MediaRecorder
declare global {
  interface MediaRecorder {
    start(): void;
    stop(): void;
    ondataavailable: ((this: MediaRecorder, ev: BlobEvent) => any) | null;
    onerror: ((this: MediaRecorder, ev: ErrorEvent) => any) | null;
    readonly state: 'inactive' | 'recording' | 'paused';
  }
}
interface MediaDevicesExtended extends MediaDevices {
  getDisplayMedia(constraints?: unknown): Promise<MediaStream>;
}

const DEFAULT_MAX_RECORDING_MINUTES = 360; // 6 hours

export interface IUseMediaRecorderProps {
  onDataAvailable?: (chunk: Blob) => Promise<void>;
  sliceInterval: number | undefined;
  maxRecordingMinutes?: number;
}

export interface IDisplayMediaProps {
  getDisplayMedia?: (chunk: Blob) => Promise<void>;
  sliceInterval: number | undefined;
}

export interface IUseMediaRecorderReturn {
  isRecording: boolean;
  errorMessage: string;
  queueSize: number;
  isProcessingQueue: boolean;
  startRecording: (includeSystemAudio: boolean, audioInputDeviceId: string | undefined) => void;
  stopRecording: () => void;
  micInputAnalyser: AnalyserNode | null;
  systemAudioAnalyser: AnalyserNode | null;
  recordingStartTime: number | null; // Unix timestamp (ms) when recording started
  abortRecording: () => void;
  // Recorded time up to the last stop, without the pauses between recordings
  getRecordedMs?: () => number;
}

function useMediaRecorder(props: IUseMediaRecorderProps) {
  const { onDataAvailable, sliceInterval, maxRecordingMinutes = DEFAULT_MAX_RECORDING_MINUTES } = props;
  const [isRecording, setIsRecording] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [audioQueue, setAudioQueue] = useState<Blob[]>([]);
  const [isProcessingQueue, setIsProcessingQueue] = useState(false);
  const [isSystemAudioIncluded, setIsSystemAudioIncluded] = useState(false);
  const [systemAudioGranted, setSystemAudioGranted] = useState(false);
  const [audioInputGranted, setAudioInputGranted] = useState(false);
  const [recordingStartTime, setRecordingStartTime] = useState<number | null>(null);
  const accumulatedRecordingMsRef = useRef<number>(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const micInputAnalyserRef = useRef<AnalyserNode | null>(null);
  const systemAudioAnalyserRef = useRef<AnalyserNode | null>(null);
  const currentAudioInputDeviceIdRef = useRef<string | undefined>(undefined);
  const { startTimer, stopTimer, resetTimer } = useElapsedTime();

  useWakeLock(isRecording);

  const setupMediaRecorder = async (includeSystemAudio: boolean, audioInputDeviceId: string | undefined) => {
    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContext();
      micInputAnalyserRef.current = audioContextRef.current.createAnalyser();
      systemAudioAnalyserRef.current = audioContextRef.current.createAnalyser();
    }
    const destination = audioContextRef.current.createMediaStreamDestination();

    if (includeSystemAudio) {
      setIsSystemAudioIncluded(true);
      const displayStream = await getSystemMediaStream();

      // Extract audio tracks from display stream
      const systemAudioTracks = displayStream.getAudioTracks();
      const systemAudioStream = new MediaStream(systemAudioTracks);

      // Connect system audio to the audio context
      const systemAudioSource = audioContextRef.current.createMediaStreamSource(systemAudioStream);
      systemAudioSource.connect(destination);
      if (systemAudioAnalyserRef.current) {
        systemAudioSource.connect(systemAudioAnalyserRef.current);
      }
    }

    const micStream = await getMicInputStream(audioInputDeviceId);

    // Connect microphone audio to the audio context
    const micSource = audioContextRef.current.createMediaStreamSource(micStream);
    micSource.connect(destination);
    if (micInputAnalyserRef.current) {
      micSource.connect(micInputAnalyserRef.current);
    }

    // Create a combined audio stream
    const combinedStream = new MediaStream(destination.stream.getTracks());

    // Initialize recorder with combined audio stream
    const recorder = new (window as any).MediaRecorder(combinedStream, { mimeType: 'audio/mp4' });

    recorder.onerror = function (event: any) {
      setErrorMessage('Der opstod en fejl under optagelse. Prøv igen.');
    };

    recorder.ondataavailable = (event: any) => {
      if (event.data.size > 0) {
        setAudioQueue(prev => [...prev, event.data]);
      }
    };

    mediaRecorderRef.current = recorder;
  };

  // Process audio queue
  useEffect(() => {
    if (!isProcessingQueue && audioQueue.length > 0) {
      setIsProcessingQueue(true);
      onDataAvailable &&
        onDataAvailable(audioQueue[0])
          .then(() => {
            setAudioQueue(audioQueue.slice(1));
            setIsProcessingQueue(false);
          })
          .catch(e => {
            // add timeout to prevent infinite loop
            setTimeout(() => {
              console.error('Retrying processing chunk'); // TODO:remove
              setIsProcessingQueue(false);
            }, 2000);
          });
    }
  }, [audioQueue, isProcessingQueue, onDataAvailable]);

  // Clear buffer at regular intervals
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    function startSliceTimer() {
      timer = setTimeout(() => {
        clearBuffer();
        if (isRecording) {
          startSliceTimer();
        }
      }, (sliceInterval ?? 60) * 1000);
    }

    if (isRecording) {
      startSliceTimer();
    }

    // Cleanup function to stop the timer
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecording]);

  // Clear buffer by stopping and restarting the recorder
  const clearBuffer = () => {
    if (isRecording) {
      mediaRecorderRef.current?.stop();
      mediaRecorderRef.current?.start();
    }
  };

  const hasInputAccess = () => audioInputGranted && (!isSystemAudioIncluded || systemAudioGranted);

  // Handle starting the MediaRecorder
  const startRecording = async (includeSystemAudio: boolean, audioInputDeviceId: string | undefined) => {
    errorMessage && setErrorMessage(null);
    const shouldSetupMediaRecorder =
      !hasInputAccess() ||
      !audioContextRef.current ||
      !mediaRecorderRef.current ||
      includeSystemAudio !== isSystemAudioIncluded ||
      (audioInputDeviceId && audioInputDeviceId !== currentAudioInputDeviceIdRef.current);

    if (shouldSetupMediaRecorder) {
      await setupMediaRecorder(includeSystemAudio, audioInputDeviceId);
    }

    if (!mediaRecorderRef.current) {
      setErrorMessage('Der opstod en fejl ved start af optagelse. Prøv igen.');
      return;
    }

    mediaRecorderRef.current.start();
    setIsRecording(true);
    setRecordingStartTime(Date.now() - accumulatedRecordingMsRef.current);
    startTimer();
  };

  // Handle stopping the MediaRecorder
  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
    setRecordingStartTime(prev => {
      if (prev !== null) {
        accumulatedRecordingMsRef.current = Date.now() - prev;
      }
      return null;
    });
    stopTimer();
    errorMessage && setErrorMessage(null);
  };

  const onAbortRecording = () => {
    mediaRecorderRef.current = null;
    setAudioQueue([]);
    setIsRecording(false);
    setIsProcessingQueue(false);
    setRecordingStartTime(null);
    accumulatedRecordingMsRef.current = 0;
    stopTimer();
    resetTimer();
    errorMessage && setErrorMessage(null);
  };

  // Check max recording time periodically
  useEffect(() => {
    if (!isRecording || !recordingStartTime) {
      return;
    }

    const checkInterval = setInterval(() => {
      const elapsedSeconds = Math.floor((Date.now() - recordingStartTime) / 1000);
      if (elapsedSeconds >= 60 * maxRecordingMinutes) {
        // Stop recording inline to avoid dependency issues
        mediaRecorderRef.current?.stop();
        accumulatedRecordingMsRef.current = Date.now() - recordingStartTime;
        setIsRecording(false);
        setRecordingStartTime(null);
        stopTimer();
        setErrorMessage('Optagelsen blev stoppet, fordi den har nået den maksimale optagetid.');
      }
    }, 1000);

    return () => clearInterval(checkInterval);
  }, [isRecording, recordingStartTime, maxRecordingMinutes, stopTimer]);

  return {
    isRecording,
    errorMessage,
    queueSize: audioQueue?.length ?? 0,
    isProcessingQueue,
    startRecording,
    stopRecording,
    micInputAnalyser: micInputAnalyserRef.current,
    systemAudioAnalyser: systemAudioAnalyserRef.current,
    recordingStartTime,
    abortRecording: onAbortRecording,
    getRecordedMs: () => accumulatedRecordingMsRef.current,
  } as IUseMediaRecorderReturn;

  async function getSystemMediaStream(): Promise<MediaStream> {
    let displayStream: MediaStream;

    const displayMediaOptions = {
      video: {
        displaySurface: 'monitor',
      },
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        sampleRate: 44100,
      },
      systemAudio: 'include',
    };

    try {
      // Capture system audio only (without video)
      displayStream = await (navigator.mediaDevices as MediaDevicesExtended).getDisplayMedia(displayMediaOptions);
    } catch (error: any) {
      console.error('Error accessing screen sharing:', error);
      setErrorMessage(getSystemAudioErrorMessage(error));
      throw error;
    }

    if (!hasSystemAudioTrack(displayStream)) {
      stopSharing(displayStream);
      setErrorMessage(missingSystemAudioTrackMessage);
      throw new Error(missingSystemAudioTrackMessage);
    }

    acceptSystemAudio(displayStream);

    return displayStream;
  }

  function acceptSystemAudio(stream: MediaStream): void {
    const [systemAudioTrack] = stream.getAudioTracks();
    systemAudioTrack.onended = () => {
      if (mediaRecorderRef.current?.state === 'recording') {
        // Indicates the screen sharing has stopped by the user
        setErrorMessage('Adgang til systemlyd er blevet afbrudt.');
        setSystemAudioGranted(false);
      }
    };
    setSystemAudioGranted(true);
  }

  async function getMicInputStream(audioInputDeviceId: string | undefined): Promise<MediaStream> {
    let micStream: MediaStream;
    try {
      // Capture microphone audio
      micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: audioInputDeviceId ? { exact: audioInputDeviceId } : undefined,
        },
      });
      currentAudioInputDeviceIdRef.current = audioInputDeviceId;
      setAudioInputGranted(true);
    } catch (error: any) {
      console.error('Error accessing microphone:', error);
      setErrorMessage('Det er ikke muligt at få adgang til mikrofonen. Kontroller at du har givet tilladelse til at bruge mikrofonen.');
      throw error;
    }
    return micStream;
  }
}

export default useMediaRecorder;
